import React, { useState, useEffect } from 'react';
import { FiMusic, FiPlus, FiRefreshCw, FiTrash2, FiHeart, FiMessageCircle, FiUsers, FiUserPlus, FiImage, FiSave, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import TikTokAnalyticsChart from '../components/TikTokAnalyticsChart';

import Loading from '../components/Loading';
const TikTokTrackerPage = () => {
  const [trackedAccounts, setTrackedAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [savingSnapshots, setSavingSnapshots] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState({});

  useEffect(() => {
    const loadAndRefresh = async () => {
      await cargarCuentas();
      // Auto-actualizar todas las cuentas al cargar la página (sin esperar)
      actualizarTodasSilent();
    };
    loadAndRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actualizarTodasSilent = async () => {
    // Versión silenciosa que no muestra toast de inicio
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      await api.post(API_BASE_URL + '/tracked-accounts/fetch_all/');
      // No recargamos las cuentas aquí para evitar múltiples recargas
    } catch (error) {
      console.log('Auto-actualización completada en segundo plano');
    }
  };

  const cargarCuentas = async () => {
    setLoading(true);
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      const response = await api.get(API_BASE_URL + '/tracked-accounts/');
      // DRF pagination: response.data.results o response.data si no hay paginación
      setTrackedAccounts(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar cuentas:', error);
      toast.error('Error al cargar las cuentas trackeadas');
    } finally {
      setLoading(false);
    }
  };

  const agregarCuenta = async () => {
    const username = newUsername.trim().replace('@', '');
    if (!username) {
      toast.error('Por favor ingresa un username');
      return;
    }

    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      await api.post(API_BASE_URL + '/tracked-accounts/', {
        username: username
      });

      toast.success('Cuenta @' + username + ' agregada exitosamente');
      setNewUsername('');
      setShowAddModal(false);
      cargarCuentas();
    } catch (error) {
      console.error('Error al agregar cuenta:', error);
      const errorMsg = error.response?.data?.username?.[0] || 'Error al agregar la cuenta';
      toast.error(errorMsg);
    }
  };

  const eliminarCuenta = async (accountId, username) => {
    if (!window.confirm('¿Estás seguro de eliminar @' + username + ' del tracking?')) {
      return;
    }

    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      await api.delete(API_BASE_URL + '/tracked-accounts/' + accountId + '/');
      toast.success('Cuenta @' + username + ' eliminada');
      cargarCuentas();
    } catch (error) {
      console.error('Error al eliminar cuenta:', error);
      toast.error('Error al eliminar la cuenta');
    }
  };

  const actualizarDatos = async (accountId, username) => {
    try {
      toast.info('Actualizando @' + username + '...');
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      await api.post(API_BASE_URL + '/tracked-accounts/' + accountId + '/fetch_data/');
      toast.success('Datos de @' + username + ' actualizados');
      cargarCuentas();
    } catch (error) {
      console.error('Error al actualizar datos:', error);
      const errorMsg = error.response?.data?.detail || 'Error al actualizar los datos';
      toast.error(errorMsg);
    }
  };

  const actualizarTodas = async () => {
    setFetchingAll(true);
    toast.info('Actualizando todas las cuentas...');
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      const response = await api.video(API_BASE_URL + '/tracked-accounts/fetch_all/');

      const successCount = response.data.success_count;
      const errorCount = response.data.error_count;
      const errorMsg = errorCount > 0 ? ', ' + errorCount + ' con errores' : '';
      toast.success(successCount + ' cuentas actualizadas' + errorMsg);
      cargarCuentas();
    } catch (error) {
      console.error('Error al actualizar todas las cuentas:', error);
      toast.error('Error al actualizar las cuentas');
    } finally {
      setFetchingAll(false);
    }
  };

  const guardarMetricas = async () => {
    setSavingSnapshots(true);
    toast.info('Actualizando cuentas y guardando métricas...');
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      const response = await api.post(API_BASE_URL + '/tracked-accounts/save_snapshots/');

      const snapshotsCreated = response.data.snapshots_created;
      const errorCount = response.data.error_count;
      const errorMsg = errorCount > 0 ? `, ${errorCount} con errores` : '';
      toast.success(`${snapshotsCreated} snapshots de métricas guardados${errorMsg}`);
      cargarCuentas();
    } catch (error) {
      console.error('Error al guardar métricas:', error);
      toast.error('Error al guardar las métricas');
    } finally {
      setSavingSnapshots(false);
    }
  };

  const limpiarVideoMetrics = async () => {
    if (!window.confirm('⚠️ Esto eliminará TODOS los datos de videos guardados. ¿Estás seguro?\n\nDespués deberás actualizar las cuentas para repoblar los datos.')) {
      return;
    }

    try {
      toast.info('Eliminando datos de videos...');
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/api\/content\/?$/i, '/api/tiktok');
      const response = await api.post(API_BASE_URL + '/tracked-accounts/clear_video_metrics/');

      if (response.data.success) {
        toast.success(`${response.data.deleted_count} registros eliminados. Ahora actualiza las cuentas.`);
        cargarCuentas();
      }
    } catch (error) {
      console.error('Error al limpiar videos:', error);
      toast.error('Error al limpiar los datos de videos');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString('es-CL');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpandAccount = (accountId) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        borderRadius: '16px',
        padding: '2rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <FiMusic size={32} style={{ color: '#EE1D52' }} />
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '2rem',
              color: '#102826',
              fontWeight: 700
            }}>
              TikTok Tracker
            </h1>
            <p style={{
              margin: '0.25rem 0 0 0',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}>
              Análisis automático de cuentas de TikTok
            </p>
          </div>
        </div>
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap'
        }}>
          {/* Botones ocultos - ahora todo se actualiza automáticamente via cron
              - update_tiktok_metrics.sh: cada 10 minutos
              - update_account_snapshots.sh: cada 24 horas (medianoche)

              Descomenta estos botones si necesitas actualización manual:

          <button
            onClick={limpiarVideoMetrics}
            title="Eliminar datos de videos para repoblar"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white'
            }}
          >
            <FiTrash2 size={18} />
            Limpiar Videos
          </button>
          <button
            onClick={actualizarTodas}
            disabled={fetchingAll || savingSnapshots || trackedAccounts.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
              color: '#374151'
            }}
          >
            <FiRefreshCw size={18} />
            {fetchingAll ? 'Actualizando...' : 'Actualizar Todas'}
          </button>
          <button
            onClick={guardarMetricas}
            disabled={fetchingAll || savingSnapshots || trackedAccounts.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white'
            }}
          >
            <FiSave size={18} />
            {savingSnapshots ? 'Guardando...' : 'Guardar Métricas'}
          </button>
          */}
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #EE1D52 0%, #69C9D0 100%)',
              color: 'white'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 12px -2px rgba(238, 29, 82, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
          >
            <FiPlus size={18} />
            Agregar Cuenta
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {trackedAccounts.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)'
            }}>
              <FiUsers color="#0288d1" size={24} />
            </div>
            <div>
              <div style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#102826'
              }}>
                {trackedAccounts.length}
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 500
              }}>
                Cuentas Trackeadas
              </div>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)'
            }}>
              <FiHeart color="#e91e63" size={24} />
            </div>
            <div>
              <div style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#102826'
              }}>
                {formatNumber(trackedAccounts.reduce((sum, acc) => sum + (acc.total_likes || 0), 0))}
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 500
              }}>
                Total Likes (Últimos 10 videos)
              </div>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)'
            }}>
              <FiMessageCircle color="#9c27b0" size={24} />
            </div>
            <div>
              <div style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#102826'
              }}>
                {formatNumber(trackedAccounts.reduce((sum, acc) => sum + (acc.total_comments || 0), 0))}
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 500
              }}>
                Total Comentarios (Últimos 10 videos)
              </div>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
            }}>
              <FiUserPlus color="#388e3c" size={24} />
            </div>
            <div>
              <div style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#102826'
              }}>
                {formatNumber(trackedAccounts.reduce((sum, acc) => sum + (acc.followers_count || 0), 0))}
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 500
              }}>
                Total Seguidores
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#EE1D52',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <Loading fullScreen={false} mensaje="Cargando cuentas" />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : trackedAccounts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <FiMusic size={64} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            No hay cuentas trackeadas
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Agrega una cuenta de TikTok para comenzar a analizar sus métricas
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #EE1D52 0%, #69C9D0 100%)',
              color: 'white'
            }}
          >
            <FiPlus /> Agregar Primera Cuenta
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1.5rem'
        }}>
          {trackedAccounts.map((account) => (
            <div
              key={account.id}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                borderRadius: '16px',
                padding: '1.5rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'hidden',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px -4px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05)';
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #EE1D52 0%, #69C9D0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  {account.profile_picture_url ? (
                    <img
                      src={account.profile_picture_url}
                      alt={account.username}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <FiMusic size={32} />
                  )}
                </div>
                <div style={{
                  flex: 1,
                  minWidth: 0
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#102826',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    @{account.username}
                  </h3>
                  {account.display_name && (
                    <p style={{
                      margin: '0.25rem 0 0 0',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}>
                      {account.display_name}
                    </p>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => actualizarDatos(account.id, account.username)}
                    title="Actualizar datos"
                    style={{
                      background: 'none',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <FiRefreshCw size={16} />
                  </button>
                  <button
                    onClick={() => eliminarCuenta(account.id, account.username)}
                    title="Eliminar"
                    style={{
                      background: 'none',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                      e.currentTarget.style.borderColor = '#fca5a5';
                      e.currentTarget.style.color = '#dc2626';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.color = '#6b7280';
                    }}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <FiUsers size={18} style={{ color: '#102826', flexShrink: 0 }} />
                  <div>
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#102826'
                    }}>
                      {formatNumber(account.followers_count)}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Seguidores
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <FiUserPlus size={18} style={{ color: '#102826', flexShrink: 0 }} />
                  <div>
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#102826'
                    }}>
                      {formatNumber(account.following_count)}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Siguiendo
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <FiImage size={18} style={{ color: '#102826', flexShrink: 0 }} />
                  <div>
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#102826'
                    }}>
                      {formatNumber(account.video_count)}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Videos
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                background: '#f9fafb',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151'
                  }}>
                    Últimos 10 Videos
                  </h4>
                  {account.recent_videos && account.recent_videos.length > 0 && (
                    <button
                      onClick={() => toggleExpandAccount(account.id)}
                      title={expandedAccounts[account.id] ? "Contraer videos" : "Expandir videos"}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.375rem 0.75rem',
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        color: '#6b7280',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.borderColor = '#d1d5db';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = '#6b7280';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      {expandedAccounts[account.id] ? (
                        <>
                          <FiChevronUp size={18} />
                          <span style={{ whiteSpace: 'nowrap' }}>Contraer</span>
                        </>
                      ) : (
                        <>
                          <FiChevronDown size={18} />
                          <span style={{ whiteSpace: 'nowrap' }}>Expandir ({account.recent_videos.length})</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {expandedAccounts[account.id] && (
                  <>
                    {account.recent_videos && account.recent_videos.length > 0 ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        marginBottom: '1rem'
                      }}>
                        {account.recent_videos.map((video, idx) => (
                          <div
                            key={video.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.75rem',
                              background: 'white',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.borderColor = '#EE1D52';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#6b7280',
                              minWidth: '24px'
                            }}>
                              #{idx + 1}
                            </div>
                            <div style={{
                              flex: 1,
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25rem'
                            }}>
                              <a
                                href={video.permalink}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  textDecoration: 'none',
                                  color: 'inherit'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.querySelector('span').style.color = '#EE1D52';
                                  e.currentTarget.querySelector('span').style.textDecoration = 'underline';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.querySelector('span').style.color = '#374151';
                                  e.currentTarget.querySelector('span').style.textDecoration = 'none';
                                }}
                              >
                                {video.caption ? (
                                  <span style={{
                                    fontSize: '0.875rem',
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                    transition: 'all 0.2s'
                                  }}>
                                    {video.caption.substring(0, 50)}{video.caption.length > 50 ? '...' : ''}
                                  </span>
                                ) : (
                                  <span style={{
                                    fontSize: '0.875rem',
                                    color: '#9ca3af',
                                    fontStyle: 'italic'
                                  }}>
                                    Sin caption
                                  </span>
                                )}
                              </a>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#9ca3af'
                              }}>
                                {video.timestamp ? new Date(video.timestamp).toLocaleDateString('es-CL') : '-'}
                              </div>
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '1rem',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#374151',
                                whiteSpace: 'nowrap'
                              }}>
                                <FiHeart color="#e91e63" size={14} />
                                {formatNumber(video.like_count)}
                              </span>
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#374151',
                                whiteSpace: 'nowrap'
                              }}>
                                <FiMessageCircle color="#9c27b0" size={14} />
                                {formatNumber(video.comments_count)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '1.5rem',
                        color: '#6b7280',
                        fontSize: '0.875rem',
                        background: 'white',
                        borderRadius: '6px',
                        marginBottom: '1rem'
                      }}>
                        <p style={{ margin: 0 }}>No hay videos recientes. Actualiza los datos de esta cuenta.</p>
                      </div>
                    )}
                  </>
                )}

                <div style={{
                  display: 'flex',
                  gap: '1.5rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}>
                    <FiHeart color="#e91e63" size={16} />
                    <span>Total: {formatNumber(account.total_likes)} likes</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}>
                    <FiMessageCircle color="#9c27b0" size={16} />
                    <span>Total: {formatNumber(account.total_comments)} comentarios</span>
                  </div>
                </div>
              </div>

              {account.last_fetched_at && (
                <div style={{
                  paddingTop: '0.75rem',
                  borderTop: '1px solid #e5e7eb',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  textAlign: 'center'
                }}>
                  <small>Última actualización: {formatDate(account.last_fetched_at)}</small>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Analytics Chart */}
      {trackedAccounts.length > 0 && (
        <TikTokAnalyticsChart accounts={trackedAccounts} />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div
          onClick={() => setShowAddModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#102826'
              }}>
                Agregar Cuenta de TikTok
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: 0,
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  color: '#374151'
                }}>
                  Username de TikTok
                </label>
                <input
                  type="text"
                  placeholder="username (sin @)"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && agregarCuenta()}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.outline = 'none';
                    e.currentTarget.style.borderColor = '#EE1D52';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(238, 29, 82, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <small style={{
                  display: 'block',
                  marginTop: '0.5rem',
                  color: '#6b7280',
                  fontSize: '0.875rem'
                }}>
                  Ingresa el username sin el símbolo @
                </small>
              </div>
            </div>
            <div style={{
              padding: '1.5rem',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem'
            }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: '#f3f4f6',
                  color: '#374151'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={agregarCuenta}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, #EE1D52 0%, #69C9D0 100%)',
                  color: 'white'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 12px -2px rgba(238, 29, 82, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                <FiPlus /> Agregar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TikTokTrackerPage;
